import { useAuth0 } from '@auth0/auth0-react';
import { Button, Message, Modal, Radio, Typography } from "@arco-design/web-react";
import { IconDown, IconUp } from "@arco-design/web-react/icon";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FetchApi, SaveApi } from "../Api/AuthenticatedApi";
import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";


import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import AccordionEditors from '../Components/AccordionEditors/AccordionEditors';
import CampaignEmails from './Editors/CampaignEmails';
import FormEditor from './Editors/FormEditor';
import CampaignSettingsEditor from './Editors/CampaignSettingsEditor';
import EmbedSnippetCard from './EmbedSnippetCard';
import FormPlacementsCard from './FormPlacementsCard';
import TestDrivePanel from './TestDrive/TestDrivePanel';
import FlowGraph from './TestDrive/FlowGraph';
import { draftDrive, templateLabels } from './TestDrive/draftDrive';
import { flowGraph } from './TestDrive/driveHelpers';
import { highlightField } from '../Components/fixHighlight';

// The campaign page has two modes. Edit: the flow as a graph, pinned on top
// (drawn from the settings as they're edited, saved or not; click a step to
// go to its settings), over the settings sections. Test: Test drive in their
// place — the same graph, playable, over their browser and inbox — on the
// saved settings. ?mode=test (or the older ?testdrive=1) opens in Test.

const COLLAPSED_KEY = "jc.flowgraph.collapsed";

// MUI -> Arco. CircularProgress -> Spin. The many unused MUI imports (Button,
// TextField, Grid, Chip, Select, FormVariantCard, SummaryCard, etc.) and the
// empty makeStyles were dropped; `classes` passed to CampaignSettingsEditor is
// now an empty object to preserve its prop interface.
const EMPTY_CLASSES = {};


export default function EditCampaign() {
    const { campaignId } = useParams();
    const [record, setRecord] = useState({});
    const [loading, setLoading] = useState(true);

    const [doubleOptIn, setDoubleOptIn] = useState(false);
    const [includeIncentive, setIncludeIncentive] = useState(false);


    const [formVariants, setFormVariants] = useState([]);
    // Bumped to refetch the campaign (e.g. after cloning a form/email variant).
    const [reloadTick, setReloadTick] = useState(0);


    const [confirmedSenderEmails, setConfirmedSenderEmails] = useState([]);

    const [allContactListItems, setAllContactListItems] = useState([]);

    // The flow as it's being set up: the settings form's values (saved or
    // not) and the reward as saved; settingsControl asks it to save first.
    const [flow, setFlow] = useState(null);
    const [incentive, setIncentive] = useState(null);
    const settingsControl = useRef(null);
    const [savePrompt, setSavePrompt] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        try { return window.localStorage.getItem(COLLAPSED_KEY) === "1"; } catch (e) { return false; }
    });
    const toggleCollapsed = () => {
        setCollapsed((c) => {
            try { window.localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1"); } catch (e) { /* storage blocked */ }
            return !c;
        });
    };


    const classes = EMPTY_CLASSES;
    const { getAccessTokenSilently } = useAuth0();
    let history = useNavigate();
    // Which accordion panel to open on load — lets callers deep-link a section,
    // e.g. the form editor's "Back to campaign" returns to ?section=forms.
    const [searchParams, setSearchParams] = useSearchParams();
    const sectionParam = searchParams.get("section");
    // Test mode: ?mode=test, or ?testdrive=1 (the email editor's preview
    // links there).
    const testMode = searchParams.get("mode") === "test" || searchParams.get("testdrive") === "1";
    // The open section follows ?section= so Test drive's "fix" links can open one.
    const openSection = sectionParam === null ? "settings" : (sectionParam === "incentives" ? "settings" : sectionParam);
    const setOpenSection = (key) => {
        const next = new URLSearchParams(searchParams);
        next.set("section", key || "");
        setSearchParams(next, { replace: true });
    };
    // A fix (a Test drive warning's, or a graph step's settings): back to
    // Edit (Test drive keeps its state), open the section it's fixed in and
    // highlight the field (or go to the contact).
    const goToFix = (step) => {
        if (step.fix === "contact") {
            history(`/contacts?searchTerm=${encodeURIComponent(step.fix_ref || "")}&open=${encodeURIComponent(step.fix_ref || "")}`);
            return;
        }
        const section = { settings: "settings", signup_email: "confirmation", forms: "forms" }[step.fix] || "settings";
        const next = new URLSearchParams(searchParams);
        next.delete("testdrive");
        next.delete("mode");
        next.set("section", section);
        setSearchParams(next, { replace: true });
        // Wait for the section to open (and the panel to slide away) first.
        setTimeout(() => {
            if (highlightField(step.fix_field)) return;
            const el = document.getElementById(`section-${section}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 350);
    };
    const setTestMode = (on) => {
        const next = new URLSearchParams(searchParams);
        next.delete("testdrive");
        if (on) next.set("mode", "test"); else next.delete("mode");
        setSearchParams(next, { replace: true });
    };
    // Test drive runs on the saved settings: unsaved changes are saved (or
    // dropped) first.
    const switchMode = (mode) => {
        if (mode !== "test") {
            setTestMode(false);
            return;
        }
        if (settingsControl.current && settingsControl.current.isDirty) {
            setSavePrompt(true);
            return;
        }
        setTestMode(true);
    };
    const saveThenTest = async () => {
        const saved = settingsControl.current ? await settingsControl.current.save() : true;
        if (saved) {
            setSavePrompt(false);
            setTestMode(true);
        }
    };
    const discardThenTest = () => {
        if (settingsControl.current) settingsControl.current.discard();
        setSavePrompt(false);
        setTestMode(true);
    };

    useEffect(() => {
        // setInitializing(true);
        FetchApi(getAccessTokenSilently,
            "/api/contact-lists/",
            (data) => {
                setAllContactListItems(data);
                // setInitializing(false);
            },
            (response) => {
                // setInitializing(false);
                history("/login");
            }, (response) => {
                // setInitializing(false);
                Message.error("Failed to load contact lists");
            })
    }, [history, getAccessTokenSilently])


    useEffect(() => {
        (async () => {
            let url = "/api/campaigns/" + campaignId
            FetchApi(getAccessTokenSilently, url, (r) => {
                setLoading(false);
                setRecord(r);
                setFormVariants(r?.form_variants || [])
                setDoubleOptIn(!!r?.double_opt_in)
                setIncludeIncentive(!!r?.include_incentive)
            }, (response) => {
                setLoading(false);
                history("/login");
            }, (response) => {
                setLoading(false);
                Message.error("Failed to load campaign");
            })
        })();
    }, [campaignId, reloadTick, setRecord, setLoading, history, getAccessTokenSilently])


    // The reward as saved, for the flow graph (the reward editor updates it
    // as it changes; it only loads once Signup Settings is opened).
    useEffect(() => {
        FetchApi(getAccessTokenSilently, "/api/campaigns/" + campaignId + "/incentive",
            (data) => setIncentive(data || null),
            () => {},
            () => setIncentive(null)); // 404: no reward set
    }, [campaignId, getAccessTokenSilently]);

    useEffect(() => {
        (async () => {
            let url = "/api/settings/confirmed-sender-emails/"
            FetchApi(getAccessTokenSilently, url, (data) => {
                // setLoading(false);
                // setRecord(data);
                setConfirmedSenderEmails(data)
            }, (response) => {
                // setLoading(false);
                history("/login");
            }, (response) => {
                // setLoading(false);
                Message.error("Failed to load sender emails");
            })
        })();
    }, [setLoading, history, getAccessTokenSilently])


    // The settings as they stand (saved or not): the settings form reports
    // them; before it has, the saved ones.
    const liveFlow = flow || {
        doubleOptIn, includeIncentive, incentiveDelivery: record.incentive_delivery, confirmedUrl: record.confirm_email_url,
        thankYouUrl: record.form_thank_you_url, senderEmailId: "" + record.sender_email_id,
    };
    // The flow graph for Edit mode, from them.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const draft = useMemo(() => draftDrive({ record, flow: liveFlow, incentive: includeIncentive ? incentive : null, senders: confirmedSenderEmails }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record, flow, incentive, includeIncentive, doubleOptIn, confirmedSenderEmails]);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const buildGraph = useMemo(() => flowGraph(draft, templateLabels(record, includeIncentive ? incentive : null, draft.double_opt_in), draft.email_problem),
        [draft, record, incentive, includeIncentive]);

    if (loading) {
        return <LoadingState />;
    }

    // saveCampaign resolves true once saved.
    function saveCampaign(data) {
        return new Promise((resolve) => {
            let url = "/api/campaigns/" + campaignId
            SaveApi(getAccessTokenSilently, url, (data) => {
                setLoading(false);
                setRecord(data);
                Message.success("Campaign saved");
                resolve(true);
            }, (response) => {
                setLoading(false);
                history("/login");
                resolve(false);
            }, (response) => {
                setLoading(false);
                Message.error("Failed to save campaign");
                resolve(false);
            }, data);
        });
    }

    // A step of the Edit graph: go to the settings behind it.
    const onBuildNode = (key) => {
        const step = buildGraph.find((n) => n.key === key);
        if (step && step.fix) goToFix(step.fix);
        else if (step) Message.info(step.why);
    };

    return (<Content>
        {/* <div
            style={{
                height: "200px",
                backgroundPosition: "center",
                backgroundSize: "cover",
                filter: "contrast(75%)",
                backgroundImage: "url(/img/wallpaper.jpeg)",
            }}
        /> */}



        <SimpleBreadcrumbs
            elements={
                [
                    { name: 'Signups', path: '/signups' },
                    { name: record.name },
                    // { name: record.name, path: `/signups/${campaignId}/view` },
                    // { name: "Edit" },
                ]
            }

        />

        {/* The flow, and the switch between building it and testing it. In
            Edit it stays pinned while the settings scroll under it. */}
        <div style={{
            position: testMode ? "static" : "sticky", top: 0, zIndex: 20, background: "var(--color-bg-1)",
            margin: "8px 0 14px", paddingBottom: 4,
        }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ minWidth: 0 }}>
                    <Typography.Text style={{ fontWeight: 600 }}>{testMode ? "Test drive" : "Your signup flow"}</Typography.Text>
                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 13 }}>
                        {testMode
                            ? "Try the whole flow as a subscriber would, on your saved settings. Nothing is saved."
                            : "Every way through your flow, as it's set up below. Switch to Test to try it."}
                    </Typography.Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {!testMode && (
                        <Button size="small" type="text" icon={collapsed ? <IconDown /> : <IconUp />} onClick={toggleCollapsed}>
                            {collapsed ? "Show the flow" : "Hide the flow"}
                        </Button>
                    )}
                    <Radio.Group type="button" value={testMode ? "test" : "build"} onChange={switchMode}
                        options={[{ label: "Edit", value: "build" }, { label: "Test", value: "test" }]} />
                </div>
            </div>
            {!testMode && !collapsed && <FlowGraph graph={buildGraph} mode="build" onNode={onBuildNode} onFix={goToFix} />}
        </div>

        <Modal
            title="Save your changes first?"
            visible={savePrompt}
            onCancel={() => setSavePrompt(false)}
            footer={(
                <>
                    <Button onClick={() => setSavePrompt(false)}>Cancel</Button>
                    <Button onClick={discardThenTest}>Discard them</Button>
                    <Button type="primary" onClick={saveThenTest}>Save and test</Button>
                </>
            )}
        >
            You've changed Signup Settings without saving. Test drive runs on the saved settings.
        </Modal>

        <TestDrivePanel campaignId={campaignId} visible={testMode} onFix={goToFix} />

        {/* Incentives now live inside Signup Settings; old links still land there. */}
        <div style={{ display: testMode ? "none" : "block" }}>
        <AccordionEditors activeKey={openSection} onChange={setOpenSection} items={[
            {
                key: "settings",
                title: "Signup Settings",
                component: (<CampaignSettingsEditor
                    classes={classes}
                    record={record}
                    doubleOptIn={doubleOptIn}
                    setDoubleOptIn={setDoubleOptIn}
                    includeIncentive={includeIncentive}
                    setIncludeIncentive={setIncludeIncentive}
                    confirmedSenderEmails={confirmedSenderEmails}
                    allContactListItems={allContactListItems}
                    setAllContactListItems={setAllContactListItems}
                    saveCampaign={saveCampaign}
                    onFlowChange={setFlow}
                    onIncentive={setIncentive}
                    onGoToEmails={() => goToFix({ fix: "signup_email", fix_field: "followup" })}
                    controlRef={settingsControl}
                />),
            },
            {
                key: "forms",
                title: "Web Forms",
                component: (<FormEditor campaignId={campaignId} formVariants={formVariants} onChanged={() => setReloadTick((t) => t + 1)} />),
            },
            {
                // The emails the flow sends: the signup email (always) and the
                // follow-up (when the reward goes by email after confirming),
                // each built-in or the campaign's own designs. The key stays
                // "confirmation" for existing ?section= links.
                key: "confirmation",
                title: "Emails",
                component: (<CampaignEmails campaignId={campaignId} record={record} flow={liveFlow} incentive={incentive}
                    onIncentive={setIncentive} onChanged={() => setReloadTick((t) => t + 1)} />),
            },
            {
                key: "embed",
                title: "Embed form",
                component: (<EmbedSnippetCard campaignId={campaignId} />),
            },
            {
                key: "placements",
                title: "Where this form appears",
                component: (<FormPlacementsCard campaignId={campaignId} />),
            },
        ]} />
        </div>

    </Content>);
}
